import React from "react";
import classNames from "classnames";
import { useDrop } from "react-dnd";
import { useContext } from "../context";
import { Props } from "./types";
import styles from "./styles.scss";

function getHitSlop(
  verticalSlop?:
    | number
    | {
        top?: number;
        bottom?: number;
      },
  horizontalSlop?:
    | number
    | {
        right?: number;
        left?: number;
      },
): {
  top: number;
  bottom: number;
  right: number;
  left: number;
} {
  return {
    left:
      (typeof horizontalSlop === "number"
        ? horizontalSlop || 0
        : typeof horizontalSlop === "object"
          ? horizontalSlop.left || 0
          : 0) * -1,
    right:
      (typeof horizontalSlop === "number"
        ? horizontalSlop || 0
        : typeof horizontalSlop === "object"
          ? horizontalSlop.right || 0
          : 0) * -1,
    top:
      (typeof verticalSlop === "number"
        ? verticalSlop || 0
        : typeof verticalSlop === "object"
          ? verticalSlop.top || 0
          : 0) * -1,
    bottom:
      (typeof verticalSlop === "number"
        ? verticalSlop || 0
        : typeof verticalSlop === "object"
          ? verticalSlop.bottom || 0
          : 0) * -1,
  };
}

function DropBox({ priority = 0, ...props }: Props) {
  const { currentPriority, setState } = useContext();
  const [{ isOver, data, canDrop }, ref] = useDrop(
    () => ({
      accept: props.acceptedTypes,
      drop: props.drop,
      canDrop: (): boolean =>
        props.isDisabled
          ? !props.isDisabled(data || { type: "NULL", payload: {} })
          : priority >= currentPriority,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
        data: monitor.getItem(),
      }),
      hover: (_, monitor) => {
        const isOver = monitor.isOver();

        isOver
          ? priority >= currentPriority &&
            setState({ currentPriority: priority })
          : setState({ currentPriority: 0 });
      },
    }),
    [props.drop, props.isDisabled, priority, currentPriority],
  );
  return (
    <div
      ref={ref as unknown as React.Ref<HTMLDivElement>}
      className={classNames(
        typeof props.className === "function"
          ? props.className(data || { type: "NULL", payload: null })
          : props.className,
        styles.dropBox,
      )}
      style={
        typeof props.style === "function"
          ? props.style(data || { type: "NULL", payload: null })
          : props.style
      }
    >
      {canDrop && (
        <div
          className={styles.hitBox}
          style={getHitSlop(props.verticalSlop, props.horizontalSlop)}
        />
      )}
      {props.children(data || { type: "NULL", payload: {} }, canDrop && isOver)}
    </div>
  );
}

export { DropBox };
